// src/admin/components/logs/ActivityLogs.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
    RiFileChartLine,
    RiCalendarLine,
    RiRefreshLine,
    RiDownloadLine,
    RiFilter2Line,
    RiCloseLine,
    RiSearchLine,
    RiTeamLine,
    RiMoneyDollarCircleLine,
    RiBuildingLine,
    RiHomeHeartLine,
    RiUserLine,
    RiShieldUserLine,
    RiSettings4Line
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import {
    getAllLogs,
    exportLogsToExcel,
    formatLogDate,
    getActionColor,
    formatCurrency,
    formatLogDetails,
    LOG_ACTION_TYPES,
    LOG_ENTITY_TYPES
} from '../../../models/logModel';

const ActivityLogs = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get params from URL
    const page = parseInt(searchParams.get('page') || '1');
    const limit = parseInt(searchParams.get('limit') || '20');
    const action = searchParams.get('action') || '';
    const entityType = searchParams.get('entityType') || '';
    const startDate = searchParams.get('startDate') || '';
    const endDate = searchParams.get('endDate') || '';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        action,
        entityType,
        startDate,
        endDate,
        limit
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                setLoading(true);
                setError(null);

                const offset = (page - 1) * limit;

                const apiFilters = {
                    limit,
                    offset
                };

                if (action) apiFilters.action = action;
                if (entityType) apiFilters.entityType = entityType;
                if (startDate) apiFilters.startDate = startDate;
                if (endDate) apiFilters.endDate = endDate;
                if (searchTerm) apiFilters.q = searchTerm;

                const response = await getAllLogs(apiFilters);
                setData(response);
            } catch (err) {
                console.error('Error fetching activity logs:', err);
                setError(err.message || 'Failed to load activity logs');
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [action, entityType, startDate, endDate, page, limit, searchTerm]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({
            ...filters,
            [name]: value
        });
    };

    const applyFilters = () => {
        const newParams = {
            page: 1,
            limit: filters.limit
        };

        if (filters.action) newParams.action = filters.action;
        if (filters.entityType) newParams.entityType = filters.entityType;
        if (filters.startDate) newParams.startDate = filters.startDate;
        if (filters.endDate) newParams.endDate = filters.endDate;

        setSearchParams(newParams);
        setFilterOpen(false);
    };

    const resetFilters = () => {
        setFilters({
            action: '',
            entityType: '',
            startDate: '',
            endDate: '',
            limit: 20
        });
    };

    const handlePageChange = (newPage) => {
        const newParams = { ...Object.fromEntries(searchParams), page: newPage };
        setSearchParams(newParams);
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            await exportLogsToExcel({
                action,
                entityType,
                startDate,
                endDate
            });
        } catch (err) {
            console.error('Error exporting logs:', err);
            setError('Failed to export logs');
        } finally {
            setExporting(false);
        }
    };

    const getEntityIcon = (type) => {
        const icons = {
            'tenant': RiTeamLine,
            'payment': RiMoneyDollarCircleLine,
            'building': RiBuildingLine,
            'apartment': RiHomeHeartLine,
            'owner': RiUserLine,
            'user': RiShieldUserLine,
            'system': RiSettings4Line
        };
        return icons[type] || RiFileChartLine;
    };

    // Filter logs if search term is used
    const filteredLogs = data?.logs?.filter(log => {
        if (!searchTerm) return true;

        const term = searchTerm.toLowerCase();
        return (
            log.userName?.toLowerCase().includes(term) ||
            log.action?.toLowerCase().includes(term) ||
            log.entityType?.toLowerCase().includes(term) ||
            log.entityName?.toLowerCase().includes(term) ||
            log.details?.toLowerCase().includes(term)
        );
    }) || [];

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                        <RiFileChartLine className={`mr-3 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                        {t('activity_logs') || 'Activity Logs'}
                    </h1>
                    <p className={`mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {t('all_user_activities') || 'View all user activities and changes'}
                    </p>
                </div>

                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => setFilterOpen(!filterOpen)}
                        className={`px-3 py-2 rounded-lg ${isDarkMode
                            ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600 border-gray-600'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                            } border flex items-center`}
                    >
                        <RiFilter2Line className="mr-2" />
                        {t('filter') || 'Filter'}
                    </button>

                    <button
                        onClick={handleExport}
                        disabled={exporting}
                        className={`px-3 py-2 rounded-lg ${isDarkMode
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-zinc-500 text-white hover:bg-red-600'
                            } flex items-center`}
                    >
                        <RiDownloadLine className="mr-2" />
                        {exporting ? t('exporting') : t('export') || 'Export'}
                    </button>
                </div>
            </div>

            {/* Search & Filter Bar */}
            <div className={`p-4 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                                <RiSearchLine className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                            </div>
                            <input
                                type="text"
                                className={`block w-full p-2.5 ps-10 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                placeholder={t('search_logs') || "Search logs..."}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {filteredLogs.length} {t('entries') || 'entries'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Panel */}
            {
                filterOpen && (
                    <div className={`p-4 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {t('filter_activities') || 'Filter Activities'}
                            </h3>
                            <button
                                onClick={() => setFilterOpen(false)}
                                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-200 text-gray-500'
                                    }`}
                            >
                                <RiCloseLine size={20} />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {t('action_type') || 'Action Type'}
                                </label>
                                <select
                                    name="action"
                                    value={filters.action}
                                    onChange={handleFilterChange}
                                    className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                >
                                    <option value="">{t('all_actions') || 'All Actions'}</option>
                                    {LOG_ACTION_TYPES.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {t(option.value) || option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {t('entity_type') || 'Entity Type'}
                                </label>
                                <select
                                    name="entityType"
                                    value={filters.entityType}
                                    onChange={handleFilterChange}
                                    className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                >
                                    <option value="">{t('all_types') || 'All Types'}</option>
                                    {LOG_ENTITY_TYPES.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {t(option.value) || option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {t('start_date') || 'Start Date'}
                                </label>
                                <input
                                    type="date"
                                    name="startDate"
                                    value={filters.startDate}
                                    onChange={handleFilterChange}
                                    className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                />
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {t('end_date') || 'End Date'}
                                </label>
                                <input
                                    type="date"
                                    name="endDate"
                                    value={filters.endDate}
                                    onChange={handleFilterChange}
                                    className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={resetFilters}
                                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                {t('reset') || 'Reset'}
                            </button>

                            <button
                                onClick={applyFilters}
                                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                                    } flex items-center`}
                            >
                                <RiFilter2Line className="mr-2" />
                                {t('apply_filter') || 'Apply Filter'}
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Loading State */}
            {
                loading && (
                    <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-red-500"></div>
                    </div>
                )
            }

            {/* Error State */}
            {
                error && !loading && (
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
                        } border`}>
                        {error}
                    </div>
                )
            }

            {/* Activity Logs Table */}
            {
                !loading && !error && data && (
                    <>
                        <div className={`rounded-xl shadow overflow-hidden ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
                            }`}>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className={`${isDarkMode ? 'bg-zinc-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                                        <tr>
                                            <th scope="col" className="px-4 py-3 text-start">{t('time') || 'Time'}</th>
                                            <th scope="col" className="px-4 py-3 text-start">{t('user') || 'User'}</th>
                                            <th scope="col" className="px-4 py-3 text-start">{t('action') || 'Action'}</th>
                                            <th scope="col" className="px-4 py-3 text-start">{t('entity') || 'Entity'}</th>
                                            <th scope="col" className="px-4 py-3 text-start">{t('details') || 'Details'}</th>
                                            <th scope="col" className="px-4 py-3 text-end">{t('amount') || 'Amount'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700 text-gray-300' : 'divide-gray-200 text-gray-700'}`}>
                                        {filteredLogs.length > 0 ? (
                                            filteredLogs.map(log => {
                                                const EntityIcon = getEntityIcon(log.entityType);
                                                return (
                                                    <tr
                                                        key={log.id}
                                                        className={`${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                                                            } transition-colors duration-150`}
                                                    >
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            {formatLogDate(log.createdAt)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center">
                                                                <RiUserLine className="mr-2 text-gray-500" />
                                                                {log.userName || 'System'}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action, isDarkMode)}`}>
                                                                {t(log.action) || log.action}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center">
                                                                <EntityIcon className="mr-2 text-gray-500" />
                                                                <div>
                                                                    <div className="font-medium">{log.entityName || '-'}</div>
                                                                    <div className="text-xs text-gray-500">{t(log.entityType) || log.entityType}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 max-w-xs md:max-w-md">
                                                            <div className={`truncate-2-lines text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} title={log.details}>
                                                                {formatLogDetails(log.details)}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-end font-medium">
                                                            {log.amount ? formatCurrency(log.amount) : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="px-4 py-6 text-center">
                                                    {searchTerm
                                                        ? t('no_logs_matching_search') || 'No logs matching your search criteria'
                                                        : t('no_logs_available') || 'No activity logs available'
                                                    }
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        {data.totalCount > filters.limit && (
                            <div className="flex justify-between items-center mt-4">
                                <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                    {t('showing_range', {
                                        start: (page - 1) * filters.limit + 1,
                                        end: Math.min(page * filters.limit, data.totalCount),
                                        total: data.totalCount
                                    }) ||
                                        `Showing ${(page - 1) * filters.limit + 1} to ${Math.min(page * filters.limit, data.totalCount)} of ${data.totalCount} entries`}
                                </div>

                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1}
                                        className={`px-3 py-1 rounded-lg ${page === 1
                                            ? isDarkMode ? 'bg-zinc-800 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-zinc-400 cursor-not-allowed'
                                            : isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                    >
                                        {t('previous') || 'Previous'}
                                    </button>

                                    <button
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === Math.ceil(data.totalCount / filters.limit)}
                                        className={`px-3 py-1 rounded-lg ${page === Math.ceil(data.totalCount / filters.limit)
                                            ? isDarkMode ? 'bg-zinc-800 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-zinc-400 cursor-not-allowed'
                                            : isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                    >
                                        {t('next') || 'Next'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )
            }
        </div >
    );
};

export default ActivityLogs;
